import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Bed, Home } from 'lucide-react';
import { ApartmentSummary } from '../types';

interface ApartmentCardProps {
  apartment: ApartmentSummary;
  to?: string;
}

const ApartmentCard: React.FC<ApartmentCardProps> = ({ apartment, to }) => {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

  return (
    <Link to={to || `/apartments/${apartment.id}`} className="block group">
      <div className="card-hover h-full flex flex-col">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          {apartment.photo_principale ? (
            <img
              src={`${baseUrl}${apartment.photo_principale.url}`}
              alt={apartment.titre}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
              <Home className="w-16 h-16 text-primary-400" />
            </div>
          )}
          
          {/* Prix badge */}
          <div className="absolute bottom-3 left-3">
            <div className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md">
              <span className="font-bold text-primary-600">{formatPrice(apartment.prix_par_nuit)}</span>
              <span className="text-neutral-500 text-sm"> FCFA/nuit</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          {/* Title */}
          <h3 className="font-semibold text-lg text-neutral-800 mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">
            {apartment.titre}
          </h3>

          {/* Location */}
          <div className="flex items-center text-neutral-500 text-sm mb-3">
            <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
            <span className="line-clamp-1">{apartment.ville}, {apartment.pays}</span>
          </div>

          {/* Description */}
          <p className="text-neutral-600 text-sm mb-4 line-clamp-2 flex-1">
            {apartment.description_courte}
          </p>

          {/* Features */}
          <div className="flex items-center gap-4 text-sm text-neutral-500 pt-3 border-t border-neutral-100">
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              <span>{apartment.capacite}</span>
            </div>
            <div className="flex items-center">
              <Bed className="w-4 h-4 mr-1" />
              <span>{apartment.nb_chambres}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ApartmentCard;
